import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X } from "lucide-react";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

export function PushNotificationBanner() {
  const { profile } = useAuth();
  const isAdmin = profile?.email === "hudsonraonefotografia@gmail.com";
  
  const { subscribe } = usePushNotifications();
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!profile || !isAdmin) return;

    const today = new Date().toDateString();
    const lastAsk = localStorage.getItem(`last_notif_ask_${profile.user_id}`);

    // Show banner if push API is available, permission hasn't been granted/denied, and we haven't asked today
    if ("Notification" in window && Notification.permission === "default" && lastAsk !== today) {
      // Delay showing the banner slightly for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [profile]);

  const handleDismiss = () => {
    if (profile) {
      localStorage.setItem(`last_notif_ask_${profile.user_id}`, new Date().toDateString());
    }
    setIsVisible(false);
  };

  const handleSubscribe = async () => {
    if (profile) {
      localStorage.setItem(`last_notif_ask_${profile.user_id}`, new Date().toDateString());
    }
    
    setLoading(true);
    try {
      await subscribe();
    } finally {
      setLoading(false);
      setIsVisible(false); // Hide the banner regardless of result, since toast will handle success/error
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -50, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          className="fixed top-4 left-4 right-4 z-[9999] max-w-md mx-auto"
        >
          <div className="relative overflow-hidden rounded-2xl bg-zinc-950/80 backdrop-blur-xl border border-white/10 shadow-2xl shadow-pink-500/10">
            
            {/* Background Glow */}
            <div className="absolute -inset-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 blur-xl -z-10" />

            <div className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-pink-500/30 flex items-center justify-center">
                <Bell className="w-6 h-6 text-pink-400 fill-pink-400/20 animate-pulse" />
              </div>

              <div className="flex-1 pr-6">
                <h3 className="text-sm font-bold text-white mb-1">
                  Ativar Notificações?
                </h3>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Receba avisos de novos álbuns, transmissões e placares ao vivo no seu dispositivo.
                </p>
              </div>

              <div className="flex gap-2 w-full sm:w-auto mt-2 sm:mt-0 shrink-0">
                <Button
                  onClick={handleSubscribe}
                  disabled={loading}
                  className="w-full sm:w-auto h-10 px-6 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-500 hover:to-pink-400 text-white font-bold text-xs shadow-lg shadow-pink-500/25 transition-all hover:scale-105 active:scale-95"
                >
                  {loading ? "Ativando..." : "Ativar"}
                </Button>
              </div>

              <button 
                onClick={handleDismiss}
                className="absolute top-3 right-3 text-zinc-500 hover:text-white transition-colors"
                aria-label="Dispensar"
              >
                <X className="w-4 h-4" />
              </button>

            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
